import { Box, Link as MuiLink, Stack, Tab } from "@mui/material"
import type { LinkProps } from "@mui/material/Link/Link"
import { type ReactNode, useContext, useEffect, useMemo } from "react"
import { Link, useLocation, useResolvedPath } from "react-router-dom"
import { SideNavCtx } from "./SideNavContext"
import { SideNavCounter } from "./SideNavCounter"

export type CounterVariants = "new" | "count" | "warn"

export type SideNavLinkProps = {
  label: ReactNode
  counter?: ReactNode
  counterVariant?: CounterVariants
  counterTotal?: number
  counterMax?: number
  disabled?: boolean
} & (
  | { to: string; href?: never; target?: never; linkProps?: never }
  | ({
      to?: never
      href: string
      target?: string
    } & Partial<Omit<LinkProps, "href" | "target" | "disabled" | "children">>)
)

export const SideNavLink = ({
  label,
  to,
  href,
  counter,
  counterTotal,
  counterVariant,
  counterMax,
  disabled = false,
  target,
  ...props
}: SideNavLinkProps) => {
  const { registerLink, setActiveLink } = useContext(SideNavCtx)

  useEffect(() => {
    if (to || href) {
      registerLink(to !== undefined ? to : href)
    }
  }, [registerLink, to, href])

  const location = useLocation()
  const path = useResolvedPath(to || "")
  const isActive = useMemo(() => {
    const locationPathname = location.pathname
    const toPathname = path.pathname
    return (
      locationPathname === toPathname ||
      (locationPathname.startsWith(toPathname) &&
        locationPathname.charAt(toPathname.length) === "/")
    )
  }, [location.pathname, path.pathname])

  useEffect(() => {
    if (to) {
      setActiveLink(to, isActive)
    }
  }, [isActive, setActiveLink, to])

  const tabLabel = (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      width="100%"
      pr={2}
    >
      <Box component="span" width="100%">
        {label}
      </Box>
      <SideNavCounter
        isActive={isActive}
        counterTotal={counterTotal}
        counter={counter}
        counterMax={counterMax}
        counterVariant={counterVariant}
      />
    </Stack>
  )

  if (to !== undefined) {
    return (
      <Tab
        {...props}
        label={tabLabel}
        to={to}
        component={Link}
        disabled={disabled}
      />
    )
  }

  return (
    <Tab
      {...props}
      label={tabLabel}
      href={href}
      component={MuiLink}
      target={target ?? "_blank"}
      disabled={!href || disabled}
    />
  )
}
